import { useEffect, useState } from "react";

/**
 * Which exam profile the app is showing: JAMB or WAEC.
 *
 * A student sitting both holds two profiles, like two accounts on one app:
 * switching changes every screen — subjects, readiness, practice, tutor. The
 * choice lives on the phone and travels with every request as `X-Exam`; the
 * server only ever honours an exam the student actually holds, and says which
 * one it used (`activeExam` on `/api/me`), which is adopted here.
 *
 * Null before the server has named one (a fresh install): requests then go
 * without the header and the server uses the student's primary exam.
 */
export type ProfileExam = "JAMB" | "WAEC";

const KEY = "prepai.exam";

/** Fired on `window` when the active exam changes, so open screens reload. */
export const EXAM_CHANGE_EVENT = "prepai:examchange";

export function isProfileExam(value: unknown): value is ProfileExam {
  return value === "JAMB" || value === "WAEC";
}

export function activeExam(): ProfileExam | null {
  try {
    const value = localStorage.getItem(KEY);
    return isProfileExam(value) ? value : null;
  } catch {
    return null;
  }
}

export function setActiveExam(exam: ProfileExam): void {
  if (activeExam() === exam) return;
  try {
    localStorage.setItem(KEY, exam);
  } catch {
    /* Storage disabled: the switch lasts as long as nothing reloads. */
  }
  window.dispatchEvent(new CustomEvent(EXAM_CHANGE_EVENT, { detail: { exam } }));
}

/**
 * Take the exam the server answered for, without announcing a switch: the data
 * that came back already belongs to it, so nothing on screen needs reloading.
 */
export function adoptActiveExam(exam: ProfileExam): void {
  if (activeExam() === exam) return;
  try {
    localStorage.setItem(KEY, exam);
  } catch {
    /* Storage disabled: requests go without the header and get the primary. */
  }
}

let knownExams: { examType: ProfileExam }[] | undefined;

/**
 * The exams the signed-in student holds, as last reported. The switcher reads
 * it while a switch is loading: the new exam's `/api/me` has not answered yet,
 * and it would list the same two exams anyway.
 */
export function rememberExams(exams: { examType: ProfileExam }[] | undefined) {
  if (exams) knownExams = exams;
  return knownExams;
}

/** Forget the choice. Run on sign-out: the next student on this phone has their own. */
export function clearActiveExam(): void {
  knownExams = undefined;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The active exam, re-rendering when it changes. */
export function useActiveExam(): ProfileExam | null {
  const [exam, setExam] = useState(activeExam);
  useEffect(() => {
    const update = () => setExam(activeExam());
    window.addEventListener(EXAM_CHANGE_EVENT, update);
    return () => window.removeEventListener(EXAM_CHANGE_EVENT, update);
  }, []);
  return exam;
}
